import React, { useRef, useState } from 'react';
import { Button, Typography, Input } from '@mui/material';
import { Container } from './styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { solidBlue } from '@/theme/colors';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { ValidateFileModal } from '@/components/Analysis/Idms/Modals/ValidateFileModal';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import * as ExcelJS from 'exceljs';

export type EsSheetData = {
  headers: string[];
  data: string[][];
  mappings?: Record<string, string>;
  valueMappings?: Record<string, Record<string, string>>;
  inlineEdits?: Record<string, string>;
  excludedRows?: number[];
  finalData?: string[][];
};

type Props = {
  onFileParsed: (data: EsSheetData, fileName: string) => void;
  onConfirmUpload?: () => void;
  onCancelUpload?: () => void;
  isUploading?: boolean;
  isValidating?: boolean;
  showConfirmation?: boolean;
  fileName?: string;
  templateFilePath?: string;
};

type FileError = { title: string; description: string };

const fileErrors = {
  typeError: {
    title: 'Incorrect File format',
    description: 'You have uploaded the incorrect file type. Please upload a .xlsx file.',
  },
  sizeError: {
    title: 'File size exceeded',
    description:
      'The file size exceeds the limit of 50MB. Please upload a file with a size less than 50MB.',
  },
  validationError: {
    title: 'Incorrect "Template headers"',
    description:
      'The template headers in the file you uploaded are incorrect. Please upload a file with the correct template headers.',
  },
  noData: {
    title: 'File has no data',
    description:
      'The file you uploaded does not have any data. Please upload a file with data to proceed.',
  },
};

export const UploadPanel = ({
  onFileParsed,
  onConfirmUpload,
  onCancelUpload,
  isUploading,
  isValidating,
  showConfirmation,
  fileName,
  templateFilePath,
}: Props) => {
  const fileInputReference = useRef<HTMLInputElement | undefined>(undefined);
  const [fileError, setFileError] = useState<FileError | undefined>(undefined);

  const parseFile = (selectedFile: File) => {
    const reader = new FileReader();

    reader.addEventListener('load', async () => {
      const arrayBuffer = reader.result as ArrayBuffer;

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const sheet = workbook.worksheets[0];

      const sheetData: string[][] = [];

      sheet.eachRow((row) => {
        const rowData: string[] = [];

        const maxCol = row.cellCount;
        for (let colIndex = 1; colIndex <= maxCol; colIndex++) {
          const cell = row.getCell(colIndex);
          rowData.push(cell.text.trim() || '');
        }

        sheetData.push(rowData);
      });

      if (sheetData.length === 0) {
        setFileError(fileErrors.noData);
        return;
      }

      const headers = sheetData[0].map((header) => header.trim());
      const data = sheetData.slice(1).map((row) => row.map((rowData) => rowData.trim()));

      onFileParsed({ headers, data }, selectedFile.name);
    });

    reader.readAsArrayBuffer(selectedFile);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const isXlsx = selectedFile.name.endsWith('.xlsx');
    const fileSize = selectedFile.size / (1024 * 1024);
    const isError = !isXlsx || fileSize > 50;
    if (isError) {
      setFileError(isXlsx ? fileErrors.sizeError : fileErrors.typeError);
      return;
    }

    const fileInput = event.target;
    fileInput.value = '';

    parseFile(selectedFile);
  };

  const handleTypeErrorDialog = () => {
    setFileError(undefined);
    if (fileInputReference.current) {
      fileInputReference.current.value = '';
    }
  };

  const handleFileValidate = () => {
    if (fileInputReference.current) {
      fileInputReference.current.click();
    }
  };

  return (
    <>
      <Container>
        <AbyssIcon iconName="import-alt" size="2xl" />
        {templateFilePath && (
          <Typography variant="h5" color="primary.500">
            Upload the file here, or download{' '}
            <Typography
              color={solidBlue}
              component="a"
              href={templateFilePath}
              download
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              template.xlsx
            </Typography>
          </Typography>
        )}
        <Button
          variant="contained"
          size="medium"
          startIcon={<AbyssIcon iconName="import-alt-wheat" size="md" />}
          onClick={handleFileValidate}
        >
          Upload File
        </Button>
        <Input
          inputRef={fileInputReference}
          hidden
          type="file"
          id="upload-file"
          style={{ display: 'none' }}
          inputProps={{ accept: '.xlsx' }}
          onChange={handleFileSelect}
        />
      </Container>

      {fileError && (
        <GenericDialog
          open={!!fileError}
          onClose={() => {}}
          title={fileError.title}
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              {fileError.description}
            </Typography>
          }
          actions={
            <Button
              variant="contained"
              onClick={() => handleTypeErrorDialog()}
              sx={containedButtonStyle}
            >
              Got It
            </Button>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}

      {isValidating && <ValidateFileModal open={isValidating} />}

      {showConfirmation && onConfirmUpload && onCancelUpload && (
        <GenericDialog
          open={showConfirmation}
          onClose={() => {}}
          title="Success"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              {`File validation complete. Please confirm to upload ${
                fileName ?? 'ES.xlsx'
              } to Abyss Fabric.`}
            </Typography>
          }
          actions={
            <>
              <Button variant="text" onClick={onCancelUpload} sx={textButtonStyle}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={onConfirmUpload}
                sx={containedButtonStyle}
                disabled={isUploading}
                startIcon={
                  <AbyssIcon
                    iconName="file-upload-line"
                    style={{ width: '1.6rem', height: '1.6rem' }}
                  />
                }
              >
                {isUploading ? 'Uploading' : 'Confirm'}
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </>
  );
};
